/** GPU Smart Sharpen — fragment-shader port of
 *  `lib/filters/smart-sharpen.ts`.
 *
 *  Same three-pass shape as Unsharp Mask (H-Gauss → V-Gauss →
 *  combine) but the combine pass operates in BT.601 YCbCr space:
 *
 *    - Convert original RGB → YCbCr (keep Cb/Cr); luma-project the
 *      blurred RGB → Y_blur (Cb/Cr of the blurred copy unused).
 *    - Compute Y diff = Y_orig - Y_blur. Threshold gate matches JS.
 *    - Halo-clamp the diff to ±haloLimit, derived from the user's
 *      halo-suppression slider (0..100 → 1.0..0.1 of full range,
 *      mirroring the CPU formula).
 *    - Y_new = Y_orig + k * clipped_diff.
 *    - Reconstruct RGB from (Y_new, original Cb, original Cr).
 *
 *  Because Gaussian blur is linear, blurring full RGB and then
 *  deriving Y_blur via the BT.601 luma weights gives the same Y
 *  channel as the CPU path (which blurs Y as a Float32 buffer
 *  directly). Avoids a separate blur of the Y channel — Pass 0/1
 *  are the exact same shaders Unsharp Mask uses, so the program
 *  cache only needs one copy of each.
 *
 *  ── Precision boundary ──────────────────────────────────────────
 *  The CPU path blurs Y as a Float32 buffer with no clamping
 *  between H and V passes. The GPU path blurs RGB through 8-bit
 *  RGBA8 framebuffer-backed textures, so each pass's output is
 *  quantized to 8 bits per channel before the next pass reads it.
 *  This means GPU Y_blur derived from the 8-bit-clamped blurRgb
 *  can drift from CPU Y_blur by up to ~0.5/255 around steep
 *  gradients. For most natural-photo content the difference is
 *  invisible; pathological synthetic gradients can produce
 *  off-by-1 byte differences in the final output. This is the
 *  same precision trade-off every blur-through-FBO shader accepts
 *  in this codebase. Full-precision intermediate FBOs (RGBA16F)
 *  would close the gap but cost 2× memory; deferred until a real
 *  use case demands it.
 *
 *  Identity (amount=0 OR radius=0) short-circuits via
 *  `applyIdentityCopy`. */

import { buildGaussianKernel1D } from '../../filters/gaussian-blur';
import type { SmartSharpenValue } from '../../filters/smart-sharpen';
import { MAX_KERNEL_RADIUS } from '../blur-shared';
import { BT601_YCBCR_GLSL } from '../glsl-helpers';
import { applyIdentityCopy } from '../identity-copy';
import { applyShaderPasses } from '../runtime';
import {
  GAUSSIAN_HORIZONTAL_FRAG,
  GAUSSIAN_VERTICAL_FRAG,
  padGaussianKernel,
} from './gaussian-blur';

/** Combine pass: luma-only sharpen + halo clamp. Operates in
 *  normalized [0, 1] space — `u_threshold` and `u_haloLimit` are
 *  pre-scaled CPU-side from their 0..255 JS values. */
const COMBINE_FRAG = `#version 300 es
precision highp float;
${BT601_YCBCR_GLSL}
in vec2 v_uv;
uniform sampler2D u_source;
uniform sampler2D u_original;
uniform float u_k;
uniform float u_threshold;
uniform float u_haloLimit;
out vec4 outColor;

void main() {
  vec4 src = texture(u_original, v_uv);
  vec3 blurRgb = texture(u_source, v_uv).rgb;

  vec3 origYCbCr = rgbToYCbCr601(src.rgb);
  // Blur is linear, so Y_blur derived from the BT.601 luma row of
  // the blurred RGB equals Y_blur computed from blurring Y directly
  // (the CPU path's approach). Avoids a separate Y-only blur pass.
  // Use the luma-only helper here — Cb/Cr of the blurred copy are
  // unused (we keep the original's chroma in the reconstruction).
  float yBlur = rgbToY601(blurRgb);
  float diff = origYCbCr.x - yBlur;

  // Threshold gate — pass Y through unchanged when |diff| <
  // threshold (smooth-region noise protection).
  if (abs(diff) < u_threshold) {
    outColor = vec4(src.rgb, src.a);
    return;
  }

  float clipped = clamp(diff, -u_haloLimit, u_haloLimit);
  float yNew = origYCbCr.x + u_k * clipped;
  vec3 rgbNew = yCbCrToRgb601(vec3(yNew, origYCbCr.y, origYCbCr.z));
  outColor = vec4(rgbNew, src.a);
}
`;

export function applySmartSharpenOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: SmartSharpenValue,
): boolean {
  const amount = Math.max(0, value.amount);
  const radius = Math.max(0, value.radius);
  if (amount === 0 || radius === 0) return applyIdentityCopy(source, target);
  const radiusInt = Math.floor(radius);
  if (radiusInt > MAX_KERNEL_RADIUS) return false;
  const threshold = Math.max(0, Math.min(255, value.threshold));
  // Mirror the CPU haloLimit formula exactly: 255 at halo=0
  // (no clamp), 25.5 at halo=100 (10% of full range).
  const halo = Math.max(0, Math.min(100, value.haloSuppression));
  const haloLimit = 255 * (1 - halo / 100) + 25.5 * (halo / 100);
  const k = amount / 100;
  const kernel = buildGaussianKernel1D(radiusInt);
  const paddedKernel = padGaussianKernel(kernel);
  const dims: [number, number] = [source.width, source.height];
  return applyShaderPasses({
    source,
    target,
    passes: [
      {
        fragShaderSrc: GAUSSIAN_HORIZONTAL_FRAG,
        uniforms: {
          u_dims: { type: 'vec2', value: dims },
          u_radius: { type: 'int', value: radiusInt },
          u_kernel: { type: 'float[]', value: paddedKernel },
        },
      },
      {
        fragShaderSrc: GAUSSIAN_VERTICAL_FRAG,
        uniforms: {
          u_dims: { type: 'vec2', value: dims },
          u_radius: { type: 'int', value: radiusInt },
          u_kernel: { type: 'float[]', value: paddedKernel },
        },
      },
      {
        fragShaderSrc: COMBINE_FRAG,
        uniforms: {
          u_k: { type: 'float', value: k },
          // Normalize 0..255 thresholds / halo limits to the [0, 1]
          // space the shader's YCbCr conversion uses.
          u_threshold: { type: 'float', value: threshold / 255 },
          u_haloLimit: { type: 'float', value: haloLimit / 255 },
        },
      },
    ],
  });
}
